import { useState, useEffect, useCallback } from "react";
import { getParams } from "../../utils/getParams";

const MESSAGE_TYPES = {
    HERO_DATA: "SPYNE_HERO_DATA",
    PRODUCTS_DATA: "SPYNE_PRODUCTS_DATA",
};

const extractOutputImages = (productsData) => {
    if (!productsData?.catalog?.imageData || !Array.isArray(productsData.catalog.imageData)) {
        return [];
    }

    return productsData.catalog.imageData
        .filter((image) => image?.output_image)
        .map((image) => image.output_image);
};

const isMediaIdValid = (payloadMediaId) => {
    const queryParams = getParams();
    const queryMediaId = queryParams?.params?.mediaId;

    // If no query mediaId, accept any payload mediaId
    if (!queryMediaId) {
        return true;
    }

    return payloadMediaId === queryMediaId;
};

export const useRealData = () => {
    const [heroImage, setHeroImage] = useState(null);
    const [productImages, setProductImages] = useState([]);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    // Update displayed images based on hero image and product images
    useEffect(() => {
        // If product images are available, show them
        if (productImages.length > 0) {
            setImages(productImages);
            setLoading(false);
        } 
        // Otherwise, show hero image if available
        else if (heroImage) {
            setImages([heroImage]);
            setLoading(false);
        }
        // If neither is available, keep loading state
        else {
            setImages([]);
            setLoading(true);
        }
    }, [heroImage, productImages]);

    const handleHeroData = useCallback((payload) => {
        if (payload?.hero_image) {
            setHeroImage(payload.hero_image);
        } else {
            setHeroImage(null);
        }
    }, []);

    const handleProductsData = useCallback((payload) => {
        if (!payload?.productsData || !payload?.mediaId) {
            return;
        }

        if (!isMediaIdValid(payload.mediaId)) {
            console.warn("[spyne] MediaId mismatch:", {
                payloadMediaId: payload.mediaId,
                queryMediaId: getParams()?.params?.mediaId,
            });
            return;
        }

        const outputImages = extractOutputImages(payload.productsData);
        
        if (outputImages.length > 0) {
            setProductImages(outputImages);
        }
    }, []);

    const handleMessage = useCallback(
        (event) => {
            try {
                const { data } = event;

                if (!data || typeof data !== "object" || !data.type) {
                    return;
                }

                switch (data.type) {
                    case MESSAGE_TYPES.HERO_DATA:
                        handleHeroData(data.payload);
                        break;

                    case MESSAGE_TYPES.PRODUCTS_DATA:
                        handleProductsData(data.payload);
                        break;

                    default:
                        // Ignore unknown message types
                        break;
                }
            } catch (error) {
                console.error("[spyne] Error handling postMessage:", error);
                setLoading(false);
            }
        },
        [handleHeroData, handleProductsData]
    );

    useEffect(() => {
        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [handleMessage]);

    // Send postMessage to parent when images are loaded
    useEffect(() => {
        if (!loading && images.length > 0 ) {
            if (window.parent && window.parent !== window) {
                try {
                    // Send message to parent window that images are loaded
                    window.parent.postMessage(
                        {
                            type: "SPYNE_IMAGES_LOADED",
                            payload: {
                                imageCount: images.length,
                                hasImages: true,
                            },
                        },
                        "*"
                    );
                } catch (error) {
                    console.error("[spyne] Failed to postMessage to parent window:", error);
                }
            }
        }
    }, [loading, images.length]);

    return { images, loading };
};

